import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Loader2, UserPlus, CheckCircle, XCircle,
  UserMinus, Crown, ChevronRight, Trash2, Search, X, Upload,
} from "lucide-react";
import { DecorativeIconPicker } from "@/components/DecorativeIconPicker";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { getTenantSlug, API_BASE } from "@/lib/api";
import { BulkImportDialog } from "@/components/BulkImportDialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";


interface TeamMember {
  id: string;
  name: string;
  jobTitle: string;
  avatarColor: string;
  avatarUrl: string | null;
  initials: string;
}

interface Team {
  id: string;
  teamName: string | null;
  leaderName: string;
  leaderTitle: string;
  leaderId: number | null;
  icon: string | null;
  members: TeamMember[];
}

interface JoinRequest {
  id: number;
  status: string;
  createdAt: string;
  requester: { id: string; name: string; jobTitle: string };
  target: { id: string; name: string; jobTitle: string };
  currentLm: { id: string; name: string } | null;
}

function useAuthHeaders() {
  const { user } = useAuth();
  const slug = getTenantSlug();
  const token = localStorage.getItem(`besthr_${slug}_token`) ?? "";
  return {
    "x-user-id": user?.id?.toString() ?? "",
    "Content-Type": "application/json",
    "x-tenant": slug,
    ...(token ? { "Authorization": `Bearer ${token}` } : {}),
  };
}

async function fetchTeams(headers: Record<string, string>): Promise<Team[]> {
  const t = encodeURIComponent(getTenantSlug());
  const res = await fetch(`${API_BASE}/api/teams/all?_t=${t}`, { cache: "no-store" as RequestCache, headers });
  if (!res.ok) throw new Error("Failed to fetch teams");
  return res.json();
}

async function fetchJoinRequests(headers: Record<string, string>): Promise<JoinRequest[]> {
  const t = encodeURIComponent(getTenantSlug());
  const res = await fetch(`${API_BASE}/api/teams/join-requests?_t=${t}`, { cache: "no-store" as RequestCache, headers });
  if (!res.ok) throw new Error("Failed to fetch join requests");
  return res.json();
}

export function TeamsTab({ defaultSubTab = "teams" }: { defaultSubTab?: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const headers = useAuthHeaders();

  const isHrOrAdmin = user?.role?.mainRole === "admin" || user?.role?.mainRole === "hr";
  const isCLevel = user?.role?.subRole === "c_level";
  const canViewTeams = isHrOrAdmin || isCLevel;
  const canManageTeams = isHrOrAdmin;

  // ── Manage team dialog ─────────────────────────────────────────────────────
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [editName, setEditName] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (selectedTeam) {
      setEditName(selectedTeam.teamName ?? "");
      setMemberSearch("");
      setConfirmDelete(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam?.id]);

  // ── Create team dialog ─────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [createTeamName, setCreateTeamName] = useState("");
  const [createLeaderId, setCreateLeaderId] = useState("");
  const [createMemberIds, setCreateMemberIds] = useState<string[]>([]);
  const [createIcon, setCreateIcon] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: teams = [], isLoading: teamsLoading, isError: teamsError } = useQuery({
    queryKey: ["teams-all"],
    queryFn: () => fetchTeams(headers),
    enabled: canViewTeams,
  });

  const { data: joinRequests = [], isLoading: requestsLoading } = useQuery({
    queryKey: ["teams-join-requests"],
    queryFn: () => fetchJoinRequests(headers),
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["teams-all"] });
    queryClient.invalidateQueries({ queryKey: ["teams-join-requests"] });
  }, [queryClient]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const moveMemberMutation = useMutation({
    mutationFn: async ({ employeeId, newLeaderId }: { employeeId: string; newLeaderId: number | null }) => {
      const res = await fetch(`${API_BASE}/api/teams/move/${employeeId}`, {
        method: "PUT", headers, body: JSON.stringify({ newLeaderId }),
      });
      if (!res.ok) throw new Error("Failed to move member");
    },
    onSuccess: () => invalidate(),
    onError: () => toast({ title: t("teamsManagement.error"), description: t("teamsManagement.errorDesc"), variant: "destructive" }),
  });

  const approveRequestMutation = useMutation({
    mutationFn: async (requestId: number) => {
      const res = await fetch(`${API_BASE}/api/teams/join-requests/${requestId}/approve`, { method: "PUT", headers });
      if (!res.ok) throw new Error("Failed to approve request");
    },
    onSuccess: () => { toast({ title: t("teamsManagement.requestApproved") }); invalidate(); },
    onError: () => toast({ title: t("teamsManagement.error"), variant: "destructive" }),
  });

  const rejectRequestMutation = useMutation({
    mutationFn: async (requestId: number) => {
      const res = await fetch(`${API_BASE}/api/teams/join-requests/${requestId}/reject`, { method: "PUT", headers });
      if (!res.ok) throw new Error("Failed to reject request");
    },
    onSuccess: () => { toast({ title: t("teamsManagement.requestRejected") }); invalidate(); },
    onError: () => toast({ title: t("teamsManagement.error"), variant: "destructive" }),
  });

  const setTeamIconMutation = useMutation({
    mutationFn: async ({ leaderId, icon }: { leaderId: number; icon: string }) => {
      const res = await fetch(`${API_BASE}/api/teams/icon/${leaderId}`, {
        method: "PUT", headers, body: JSON.stringify({ icon }),
      });
      if (!res.ok) throw new Error("Failed to update icon");
    },
    onSuccess: () => invalidate(),
    onError: () => toast({ title: t("teamsManagement.error"), variant: "destructive" }),
  });

  // ── Derived data ──────────────────────────────────────────────────────────
  const benchTeam = teams.find((tm) => tm.id === "bench");
  const actualTeams = teams.filter((tm) => tm.id !== "bench");
  const allMembers: TeamMember[] = teams.flatMap((tm) => tm.members);
  const leaderAsMembers: TeamMember[] = actualTeams
    .filter((tm) => tm.leaderId != null)
    .map((tm) => ({
      id: String(tm.leaderId!),
      name: tm.leaderName,
      jobTitle: tm.leaderTitle,
      avatarColor: "#6366f1",
      avatarUrl: null,
      initials: tm.leaderName.split(" ").filter(Boolean).map((p) => p[0]).join("").slice(0, 2).toUpperCase(),
    }));
  const allEmployeesForCreate: TeamMember[] = [
    ...allMembers,
    ...leaderAsMembers.filter((l) => !allMembers.some((e) => e.id === l.id)),
  ].sort((a, b) => a.name.localeCompare(b.name));

  const eligibleToAdd = selectedTeam
    ? allEmployeesForCreate.filter((e) => {
        const inTeam = selectedTeam.members.some((m) => m.id === e.id);
        const isLeader = String(selectedTeam.leaderId) === e.id;
        return !inTeam && !isLeader;
      })
    : [];

  // ── Handlers: manage dialog ───────────────────────────────────────────────
  async function handleSaveTeam() {
    if (!selectedTeam?.leaderId) return;
    setIsSaving(true);
    try {
      if (editName.trim() !== (selectedTeam.teamName ?? "")) {
        const res = await fetch(`${API_BASE}/api/teams/rename/${selectedTeam.leaderId}`, {
          method: "PUT", headers, body: JSON.stringify({ teamName: editName.trim() || null }),
        });
        if (!res.ok) throw new Error("Failed to rename team");
      }
      toast({ title: "Team saved" });
      invalidate();
      setSelectedTeam(null);
    } catch (err: any) {
      toast({ title: t("teamsManagement.error"), description: err.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRemoveMember(memberId: string) {
    await moveMemberMutation.mutateAsync({ employeeId: memberId, newLeaderId: null });
    setSelectedTeam((prev) =>
      prev ? { ...prev, members: prev.members.filter((m) => m.id !== memberId) } : null
    );
  }

  async function handleAddMember(empId: string) {
    if (!empId || !selectedTeam?.leaderId) return;
    await moveMemberMutation.mutateAsync({ employeeId: empId, newLeaderId: selectedTeam.leaderId });
    const newMember = allEmployeesForCreate.find((e) => e.id === empId);
    if (newMember) {
      setSelectedTeam((prev) =>
        prev ? { ...prev, members: [...prev.members, newMember] } : null
      );
    }
  }

  async function handleDeleteTeam() {
    if (!selectedTeam?.leaderId) return;
    setIsSaving(true);
    try {
      await Promise.all(
        selectedTeam.members.map((m) =>
          fetch(`${API_BASE}/api/teams/move/${m.id}`, { method: "PUT", headers, body: JSON.stringify({ newLeaderId: null }) })
        )
      );
      const res = await fetch(`${API_BASE}/api/teams/remove-leader/${selectedTeam.leaderId}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Failed to delete team");
      toast({ title: "Team deleted" });
      invalidate();
      setSelectedTeam(null);
    } catch (err: any) {
      toast({ title: t("teamsManagement.error"), description: err.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  }

  // ── Handler: create team ──────────────────────────────────────────────────
  async function handleCreateTeam() {
    if (!createLeaderId) return;
    setIsCreating(true);
    try {
      const leaderEmpId = Number(createLeaderId);
      const r1 = await fetch(`${API_BASE}/api/teams/assign-leader/${leaderEmpId}`, { method: "PUT", headers });
      if (!r1.ok) throw new Error("Failed to assign leader");

      if (createTeamName.trim()) {
        const r2 = await fetch(`${API_BASE}/api/teams/rename/${leaderEmpId}`, {
          method: "PUT", headers, body: JSON.stringify({ teamName: createTeamName.trim() }),
        });
        if (!r2.ok) throw new Error("Failed to set team name");
      }

      for (const memberId of createMemberIds) {
        await fetch(`${API_BASE}/api/teams/move/${memberId}`, {
          method: "PUT", headers, body: JSON.stringify({ newLeaderId: leaderEmpId }),
        });
      }

      if (createIcon) {
        await fetch(`${API_BASE}/api/teams/icon/${leaderEmpId}`, {
          method: "PUT", headers, body: JSON.stringify({ icon: createIcon }),
        });
      }

      toast({ title: t("teamsManagement.leaderAssigned") });
      invalidate();
      setCreateOpen(false);
      setCreateTeamName("");
      setCreateLeaderId("");
      setCreateMemberIds([]);
      setCreateIcon(null);
    } catch (err: any) {
      toast({ title: t("teamsManagement.error"), description: err.message, variant: "destructive" });
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue={defaultSubTab}>
        <TabsList>
          {canViewTeams && (
            <TabsTrigger value="teams">{t("teamsManagement.teamsTab")}</TabsTrigger>
          )}
          <TabsTrigger value="requests">{t("teamsManagement.requestsTab")}</TabsTrigger>
        </TabsList>

        {/* ── Teams Tab ─────────────────────────────────────────────────────── */}
        {canViewTeams && (
          <TabsContent value="teams" className="space-y-4 mt-4">
            {canManageTeams && (
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2">
                  <Upload className="h-4 w-4" />
                  {t("bulkImport.importCsv", "Upload Excel")}
                </Button>
                <Button onClick={() => setCreateOpen(true)} className="gap-2">
                  <UserPlus className="h-4 w-4" />
                  {t("teamsManagement.createTeam")}
                </Button>
              </div>
            )}

            {canManageTeams && (
              <BulkImportDialog
                open={importOpen}
                onClose={() => setImportOpen(false)}
                entity="teams"
                onComplete={invalidate}
              />
            )}

            {teamsLoading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                {t("common.loading")}
              </div>
            ) : teamsError ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <p className="text-sm">{t("common.errorLoading", { defaultValue: "Failed to load teams. Please refresh the page." })}</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {actualTeams.map((team) => (
                    <div
                      key={team.id}
                      onClick={canManageTeams ? () => setSelectedTeam(team) : undefined}
                      className={`relative overflow-hidden bg-card border border-border rounded-xl p-4 shadow-sm transition-all group${canManageTeams ? " cursor-pointer hover:shadow-md hover:border-primary/40 hover:bg-accent/30" : ""}`}
                    >
                      <DecorativeIconPicker
                        value={team.icon}
                        defaultIconKey="users"
                        onSelect={(key) => {
                          if (team.leaderId != null) setTeamIconMutation.mutate({ leaderId: team.leaderId, icon: key });
                        }}
                      />
                      <div className="relative z-10 flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-base truncate">
                            {team.teamName || t("teams.defaultName", { name: team.leaderName })}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
                            <Crown className="h-3 w-3 text-amber-500 shrink-0" />
                            <span className="truncate">{team.leaderName}</span>
                            <span className="text-muted-foreground/40">·</span>
                            <span>{team.members.length} {t("teamsManagement.employees")}</span>
                          </div>
                        </div>
                        {canManageTeams && (
                          <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bench */}
                {benchTeam && benchTeam.members.length > 0 && (
                  <div className="bg-card border border-dashed border-border rounded-xl p-4">
                    <div className="mb-3">
                      <p className="font-semibold text-base text-muted-foreground">{t("teamsManagement.bench")}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {benchTeam.members.length} {t("teamsManagement.employees")} · {t("teamsManagement.benchDesc")}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {benchTeam.members.slice(0, 8).map((member) => (
                        <div key={member.id} className="flex items-center gap-1 bg-muted/50 rounded-md px-1.5 py-0.5">
                          <EmployeeAvatar avatarUrl={member.avatarUrl} avatarColor={member.avatarColor} initials={member.initials} name={member.name} size="sm" className="h-4 w-4 text-[7px]" />
                          <span className="text-[11px] font-medium">{member.name.split(" ")[0]}</span>
                        </div>
                      ))}
                      {benchTeam.members.length > 8 && (
                        <span className="text-[11px] text-muted-foreground">{t("common.moreCount", { count: benchTeam.members.length - 8 })}</span>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        )}

        {/* ── Requests Tab ──────────────────────────────────────────────────── */}
        <TabsContent value="requests" className="mt-4">
          <div className="border border-border rounded-xl overflow-hidden bg-card shadow-sm">
            <div className="px-5 py-4 border-b border-border">
              <p className="font-semibold text-base">{t("teamsManagement.pendingRequests")}</p>
            </div>
            <div className="p-4">
              {requestsLoading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  {t("common.loading")}
                </div>
              ) : joinRequests.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">{t("teamsManagement.noRequests")}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("teamsManagement.requester")}</TableHead>
                        <TableHead>{t("teamsManagement.targetEmployee")}</TableHead>
                        <TableHead>{t("teamsManagement.currentLm")}</TableHead>
                        <TableHead>{t("teamsManagement.date")}</TableHead>
                        <TableHead className="text-right">{t("common.actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {joinRequests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{request.requester.name}</p>
                              <p className="text-xs text-muted-foreground">{request.requester.jobTitle}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{request.target.name}</p>
                              <p className="text-xs text-muted-foreground">{request.target.jobTitle}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm text-muted-foreground">{request.currentLm?.name ?? t("teamsManagement.none")}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm text-muted-foreground">{new Date(request.createdAt).toLocaleDateString()}</p>
                          </TableCell>
                          <TableCell className="text-right">
                            {canManageTeams ? (
                              <div className="flex items-center justify-end gap-2">
                                <Button variant="outline" size="sm" className="gap-1.5 text-green-600 hover:text-green-700 border-green-200 hover:border-green-300"
                                  onClick={() => approveRequestMutation.mutate(request.id)} disabled={approveRequestMutation.isPending}>
                                  <CheckCircle className="h-3.5 w-3.5" />
                                  {t("teamsManagement.approve")}
                                </Button>
                                <Button variant="outline" size="sm" className="gap-1.5 text-destructive hover:text-destructive border-destructive/20 hover:border-destructive/40"
                                  onClick={() => rejectRequestMutation.mutate(request.id)} disabled={rejectRequestMutation.isPending}>
                                  <XCircle className="h-3.5 w-3.5" />
                                  {t("teamsManagement.reject")}
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Manage Team Dialog ────────────────────────────────────────────────── */}
      <Dialog open={!!selectedTeam} onOpenChange={(open) => { if (!open) setSelectedTeam(null); }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shadow-md shrink-0">
                <Crown className="h-5 w-5 text-white" />
              </div>
              <div>
                <DialogTitle>{selectedTeam?.teamName || (selectedTeam ? t("teams.defaultName", { name: selectedTeam.leaderName }) : t("teams.manageTeam"))}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-0.5">{selectedTeam?.members.length ?? 0} members</p>
              </div>
            </div>
          </DialogHeader>

          {/* Two-column body */}
          <div className="flex-1 flex overflow-hidden min-h-0">

            {/* ── Left: team details ── */}
            <div className="flex flex-col w-80 shrink-0 border-r border-border overflow-y-auto px-5 py-5 gap-5">

              {/* Team name */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Team Name</label>
                <div className="flex items-center gap-2">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder={selectedTeam ? t("teams.defaultName", { name: selectedTeam.leaderName }) : t("teams.namePlaceholder")}
                    className="h-10"
                  />
                  <DecorativeIconPicker
                    variant="inline"
                    value={selectedTeam?.icon}
                    defaultIconKey="users"
                    onSelect={(key) => {
                      if (selectedTeam?.leaderId != null) {
                        setTeamIconMutation.mutate({ leaderId: selectedTeam.leaderId, icon: key });
                        setSelectedTeam((prev) => (prev ? { ...prev, icon: key } : prev));
                      }
                    }}
                    className="h-10 w-10 rounded-xl border border-input"
                  />
                </div>
              </div>

              {/* Leader */}
              {selectedTeam && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Team Leader</label>
                  <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <Crown className="h-4 w-4 text-amber-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-none truncate">{selectedTeam.leaderName}</p>
                      {selectedTeam.leaderTitle && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{selectedTeam.leaderTitle}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Delete — pushed to bottom */}
              <div className="mt-auto pt-4 border-t border-border">
                {!confirmDelete ? (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="text-xs text-destructive hover:text-destructive/80 transition-colors flex items-center gap-1.5 font-medium"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete this team
                  </button>
                ) : (
                  <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 space-y-2">
                    <p className="text-sm text-destructive font-medium">Delete this team?</p>
                    <p className="text-xs text-muted-foreground">All members will be moved to the bench. This cannot be undone.</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" onClick={handleDeleteTeam} disabled={isSaving} className="h-8">
                        {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Yes, delete"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmDelete(false)} className="h-8">
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Right: members ── */}
            {selectedTeam && (() => {
              const lc = memberSearch.toLowerCase();
              const currentMemberIds = new Set(selectedTeam.members.map((m) => m.id));
              const leaderIdStr = String(selectedTeam.leaderId);
              const allVisible = allEmployeesForCreate.filter(
                (e) => e.id !== leaderIdStr &&
                  (!lc || e.name.toLowerCase().includes(lc) || (e.jobTitle || "").toLowerCase().includes(lc))
              );
              const inTeam = allVisible.filter((e) => currentMemberIds.has(e.id));
              const notInTeam = allVisible.filter((e) => !currentMemberIds.has(e.id));

              return (
                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                  {/* Search header */}
                  <div className="px-5 pt-5 pb-3 shrink-0 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">
                        Members
                        <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                          ({selectedTeam.members.length})
                        </span>
                      </span>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                      <input
                        type="text"
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                        placeholder="Search employees…"
                        className="w-full h-9 pl-8 pr-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      {memberSearch && (
                        <button
                          onClick={() => setMemberSearch("")}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Unified scrollable list */}
                  <div className="flex-1 overflow-y-auto border-t border-border divide-y divide-border">
                    {inTeam.length === 0 && notInTeam.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic px-5 py-6 text-center">
                        {memberSearch ? "No employees match your search" : t("teamsManagement.noMembers")}
                      </p>
                    ) : (
                      <>
                        {/* Current members */}
                        {inTeam.map((emp) => (
                          <div key={emp.id} className="flex items-center gap-3 px-5 py-2.5 bg-primary/[0.03]">
                            <EmployeeAvatar avatarUrl={emp.avatarUrl} avatarColor={emp.avatarColor} initials={emp.initials} name={emp.name} size="sm" className="h-7 w-7 text-[10px] shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium leading-none truncate">{emp.name}</p>
                              {emp.jobTitle && <p className="text-xs text-muted-foreground mt-0.5 truncate">{emp.jobTitle}</p>}
                            </div>
                            <button
                              onClick={() => handleRemoveMember(emp.id)}
                              disabled={moveMemberMutation.isPending}
                              className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                              title="Remove from team"
                            >
                              <UserMinus className="h-4 w-4" />
                            </button>
                          </div>
                        ))}

                        {/* Eligible to add */}
                        {notInTeam.length > 0 && (
                          <>
                            {inTeam.length > 0 && (
                              <div className="px-5 py-1.5 bg-muted/30">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Add to team</span>
                              </div>
                            )}
                            {notInTeam.map((emp) => (
                              <div key={emp.id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-muted/30 transition-colors">
                                <EmployeeAvatar avatarUrl={emp.avatarUrl} avatarColor={emp.avatarColor} initials={emp.initials} name={emp.name} size="sm" className="h-7 w-7 text-[10px] shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium leading-none truncate">{emp.name}</p>
                                  {emp.jobTitle && <p className="text-xs text-muted-foreground mt-0.5 truncate">{emp.jobTitle}</p>}
                                </div>
                                <button
                                  onClick={() => handleAddMember(emp.id)}
                                  disabled={moveMemberMutation.isPending}
                                  className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                                  title="Add to team"
                                >
                                  <UserPlus className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setSelectedTeam(null)}>Cancel</Button>
            <Button onClick={handleSaveTeam} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create Team Dialog ─────────────────────────────────────────────── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>{t("teamsManagement.createTeam")}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Team Name</label>
              <div className="flex items-center gap-2">
                <Input
                  value={createTeamName}
                  onChange={(e) => setCreateTeamName(e.target.value)}
                  placeholder="e.g. Engineering, Sales…"
                  className="h-11"
                />
                <DecorativeIconPicker
                  variant="inline"
                  value={createIcon}
                  defaultIconKey="users"
                  onSelect={(key) => setCreateIcon(key)}
                  className="h-11 w-11 rounded-xl border border-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Team Leader <span className="text-destructive">*</span>
              </label>
              <Select
                value={createLeaderId}
                onValueChange={(v) => {
                  setCreateLeaderId(v);
                  setCreateMemberIds((prev) => prev.filter((id) => id !== v));
                }}
              >
                <SelectTrigger className="w-full h-11">
                  <SelectValue placeholder="Select a team leader…" />
                </SelectTrigger>
                <SelectContent>
                  {allEmployeesForCreate.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}{emp.jobTitle ? ` — ${emp.jobTitle}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Members{" "}
                <span className="text-muted-foreground font-normal text-xs">
                  (optional{createMemberIds.length > 0 ? `, ${createMemberIds.length} selected` : ""})
                </span>
              </label>
              {allEmployeesForCreate.filter((e) => e.id !== createLeaderId).length === 0 ? (
                <p className="text-sm text-muted-foreground italic py-2">No other employees available.</p>
              ) : (
                <div className="border border-border rounded-xl divide-y divide-border max-h-48 overflow-y-auto">
                  {allEmployeesForCreate
                    .filter((emp) => emp.id !== createLeaderId)
                    .map((emp) => (
                      <label
                        key={emp.id}
                        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary/40 transition-colors"
                      >
                        <Checkbox
                          checked={createMemberIds.includes(emp.id)}
                          onCheckedChange={(checked) =>
                            setCreateMemberIds((prev) =>
                              checked ? [...prev, emp.id] : prev.filter((id) => id !== emp.id)
                            )
                          }
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium leading-none truncate">{emp.name}</p>
                          {emp.jobTitle && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{emp.jobTitle}</p>
                          )}
                        </div>
                      </label>
                    ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleCreateTeam} disabled={!createLeaderId || isCreating}>
              {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("teamsManagement.createTeam")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
